import { useState, useEffect } from "react";
import { uid, T, mono } from "./utils";
import { supabase } from "./supabase";

const COLORS = ["green","amber","red"];
const COLOR_MAP = {
  green:  { text:"#1D9E75", bg:"#eafaf4", border:"#a8e6cf" },
  amber:  { text:"#BA7517", bg:"#fdf6e3", border:"#f5d78e" },
  red:    { text:"#A32D2D", bg:"#fdf0f0", border:"#f0b8b8" },
};
const STATUS_STYLE = {
  "bekleniyor": { bg:"#FEF3C7", color:"#92400E", border:"#FCD34D" },
  "ödendi":     { bg:"#D1FAE5", color:"#065F46", border:"#6EE7B7" },
  "geldi":      { bg:"#D1FAE5", color:"#065F46", border:"#6EE7B7" },
};

function Badge({ status }) {
  if (!status) return null;
  const s = STATUS_STYLE[status] || { bg:"#f3f4f6", color:"#6b7280", border:"#e5e7eb" };
  return (
    <span style={{ fontSize:10, padding:"2px 8px", borderRadius:20, fontWeight:500, background:s.bg, color:s.color, border:`1px solid ${s.border}`, whiteSpace:"nowrap", flexShrink:0 }}>
      {status}
    </span>
  );
}

function InlineText({ value, onSave, style={}, placeholder="" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(() => setVal(value), [value]);
  const commit = () => { setEditing(false); if (val !== value) onSave(val); };
  if (editing) return (
    <input value={val} onChange={e => setVal(e.target.value)}
      onBlur={commit} onKeyDown={e => { if (e.key==="Enter") commit(); if (e.key==="Escape") { setVal(value); setEditing(false); } }}
      autoFocus placeholder={placeholder}
      style={{ ...style, border:`1px solid #4a6fa5`, borderRadius:3, padding:"1px 6px", outline:"none", fontFamily:"'Inter',sans-serif", background:"#fff", minWidth:60 }} />
  );
  return (
    <span onDoubleClick={() => setEditing(true)} title="Düzenle"
      style={{ ...style, cursor:"text" }}>
      {value || <span style={{ color:"#ccc" }}>{placeholder}</span>}
    </span>
  );
}

function GroupCard({ group, rows, onUpdateRow, onDeleteRow, onAddRow, onDeleteGroup, onRenameGroup }) {
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(group);
  const [hovered, setHovered] = useState(null);

  const normalRows = rows.filter(r => !r.is_total && !r.is_note);
  const totalRows = rows.filter(r => r.is_total);
  const noteRows = rows.filter(r => r.is_note);

  const commitName = () => {
    setEditingName(false);
    if (nameVal.trim() && nameVal !== group) onRenameGroup(group, nameVal.trim());
  };

  return (
    <div style={{ background:"#fff", border:`1px solid ${T.border}`, borderRadius:6, overflow:"hidden", breakInside:"avoid" }}>
      {/* Card header */}
      <div style={{ padding:"12px 16px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"center", background:T.bg }}>
        {editingName ? (
          <input value={nameVal} onChange={e=>setNameVal(e.target.value)} onBlur={commitName}
            onKeyDown={e=>{if(e.key==="Enter")commitName();if(e.key==="Escape"){setNameVal(group);setEditingName(false);}}}
            autoFocus style={{...mono,fontSize:10,color:"#888",border:`1px solid #4a6fa5`,borderRadius:2,padding:"1px 6px",outline:"none",fontFamily:"'JetBrains Mono',monospace",textTransform:"uppercase",letterSpacing:"0.08em",background:"#fff"}}/>
        ) : (
          <span onDoubleClick={()=>setEditingName(true)} style={{...mono,fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:"0.08em",cursor:"text"}}>{group}</span>
        )}
        <div style={{display:"flex",gap:6}}>
          <button onClick={()=>onAddRow(group)} style={{...mono,fontSize:8,background:"none",border:`1px solid ${T.border}`,borderRadius:2,padding:"2px 8px",cursor:"pointer",color:T.mute}}>+ satır</button>
          <button onClick={()=>onDeleteGroup(group)} style={{...mono,fontSize:8,background:"none",border:"none",color:"#e0b8b8",cursor:"pointer",padding:0,opacity:0.5}} onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.5"}>sil</button>
        </div>
      </div>

      {/* Normal rows */}
      {normalRows.map((r,i) => {
        const c = COLOR_MAP[r.color]||COLOR_MAP.green;
        return (
          <div key={r.id}
            onMouseEnter={()=>setHovered(r.id)} onMouseLeave={()=>setHovered(null)}
            style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",borderBottom:i<normalRows.length-1?`1px solid ${T.borderLight}`:"none",background:hovered===r.id?"#fafaf8":"#fff"}}>
            {/* Color dot */}
            <div style={{display:"flex",gap:3,flexShrink:0}}>
              {COLORS.map(col=>(
                <span key={col} onClick={()=>onUpdateRow(r.id,{color:col})}
                  style={{width:7,height:7,borderRadius:"50%",background:COLOR_MAP[col].text,cursor:"pointer",opacity:r.color===col?1:0.25,transform:r.color===col?"scale(1.3)":"scale(1)",transition:"all .1s"}}/>
              ))}
            </div>
            {/* Label + sublabel */}
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                <InlineText value={r.label} onSave={v=>onUpdateRow(r.id,{label:v})} style={{fontSize:13,color:T.ink}} placeholder="Etiket…"/>
                <Badge status={r.status}/>
              </div>
              {r.sublabel && <div style={{fontSize:11,color:"#999",marginTop:1}}>{r.sublabel}</div>}
              {/* Editable sublabel */}
              <InlineText value={r.sublabel||""} onSave={v=>onUpdateRow(r.id,{sublabel:v})} style={{fontSize:11,color:"#bbb",display:r.sublabel?"none":"block"}} placeholder="alt açıklama ekle…"/>
            </div>
            {/* Amount */}
            <InlineText value={r.amount} onSave={v=>onUpdateRow(r.id,{amount:v})}
              style={{fontSize:14,fontWeight:600,color:c.text,textAlign:"right",flexShrink:0,minWidth:80}} placeholder="miktar…"/>
            {/* Status */}
            <select value={r.status} onChange={e=>onUpdateRow(r.id,{status:e.target.value})}
              style={{...mono,fontSize:9,background:"none",border:`1px solid ${T.border}`,borderRadius:2,padding:"2px 4px",cursor:"pointer",color:T.mute,fontFamily:"'JetBrains Mono',monospace",flexShrink:0}}>
              <option value="">—</option>
              <option value="bekleniyor">bekleniyor</option>
              <option value="ödendi">ödendi</option>
              <option value="geldi">geldi</option>
            </select>
            {/* Delete */}
            <button onClick={()=>onDeleteRow(r.id)}
              style={{opacity:hovered===r.id?0.6:0,background:"none",border:"none",color:"#c0392b",fontSize:14,cursor:"pointer",padding:0,flexShrink:0,transition:"opacity .15s"}}>×</button>
          </div>
        );
      })}

      {/* Note rows */}
      {noteRows.map(r => {
        const c = COLOR_MAP[r.color]||COLOR_MAP.red;
        return (
          <div key={r.id} onMouseEnter={()=>setHovered(r.id)} onMouseLeave={()=>setHovered(null)}
            style={{display:"flex",alignItems:"center",gap:10,padding:"8px 16px",background:c.bg,borderTop:`1px solid ${T.borderLight}`}}>
            <div style={{flex:1,minWidth:0}}>
              <InlineText value={r.label} onSave={v=>onUpdateRow(r.id,{label:v})} style={{fontSize:12,color:c.text,fontStyle:"italic"}} placeholder="Not…"/>
              {r.sublabel&&<div style={{fontSize:11,color:c.text,opacity:0.7,marginTop:1}}>{r.sublabel}</div>}
            </div>
            <InlineText value={r.amount} onSave={v=>onUpdateRow(r.id,{amount:v})}
              style={{fontSize:13,fontWeight:600,color:c.text,textAlign:"right",flexShrink:0}} placeholder="—"/>
            <button onClick={()=>onDeleteRow(r.id)}
              style={{opacity:hovered===r.id?0.6:0,background:"none",border:"none",color:"#c0392b",fontSize:14,cursor:"pointer",padding:0,transition:"opacity .15s"}}>×</button>
          </div>
        );
      })}

      {/* Total rows */}
      {totalRows.map(r => {
        const c = COLOR_MAP[r.color]||COLOR_MAP.green;
        return (
          <div key={r.id} onMouseEnter={()=>setHovered(r.id)} onMouseLeave={()=>setHovered(null)}
            style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",borderTop:`2px solid ${T.border}`,background:T.bg}}>
            <InlineText value={r.label} onSave={v=>onUpdateRow(r.id,{label:v})}
              style={{fontSize:13,fontWeight:500,color:T.ink}} placeholder="Toplam…"/>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <InlineText value={r.amount} onSave={v=>onUpdateRow(r.id,{amount:v})}
                style={{fontSize:16,fontWeight:700,color:c.text}} placeholder="—"/>
              <button onClick={()=>onDeleteRow(r.id)}
                style={{opacity:hovered===r.id?0.5:0,background:"none",border:"none",color:"#c0392b",fontSize:14,cursor:"pointer",padding:0,transition:"opacity .15s"}}>×</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function CurrentView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const { data } = await supabase.from("financial_snapshot").select("*").order("group_order").order("row_order");
    setRows(data || []);
    setLoading(false);
  }

  async function updateRow(id, patch) {
    setRows(prev => prev.map(r => r.id===id ? {...r,...patch} : r));
    await supabase.from("financial_snapshot").update(patch).eq("id", id);
  }

  async function deleteRow(id) {
    setRows(prev => prev.filter(r => r.id!==id));
    await supabase.from("financial_snapshot").delete().eq("id", id);
  }

  async function addRow(groupName) {
    const gRows = rows.filter(r=>r.group_name===groupName);
    const groupOrder = gRows.length>0 ? gRows[0].group_order : 0;
    const maxOrder = Math.max(...gRows.map(r=>r.row_order), -1);
    const nr = { id:uid(), group_name:groupName, group_order:groupOrder, label:"Yeni satır", sublabel:"", amount:"", color:"green", status:"", is_total:false, is_note:false, row_order:maxOrder+1 };
    setRows(prev => [...prev, nr]);
    await supabase.from("financial_snapshot").insert(nr);
  }

  async function addGroup() {
    if (!newGroupName.trim()) return;
    const maxOrder = rows.length>0 ? Math.max(...rows.map(r=>r.group_order)) + 1 : 0;
    const nr = { id:uid(), group_name:newGroupName.trim(), group_order:maxOrder, label:"Yeni satır", sublabel:"", amount:"", color:"green", status:"", is_total:false, is_note:false, row_order:0 };
    setRows(prev => [...prev, nr]);
    await supabase.from("financial_snapshot").insert(nr);
    setNewGroupName(""); setAddingGroup(false);
  }

  async function deleteGroup(groupName) {
    if (!window.confirm(`"${groupName}" grubunu silmek istediğine emin misin?`)) return;
    setRows(prev => prev.filter(r => r.group_name!==groupName));
    await supabase.from("financial_snapshot").delete().eq("group_name", groupName);
  }

  async function renameGroup(oldName, newName) {
    setRows(prev => prev.map(r => r.group_name===oldName ? {...r, group_name:newName} : r));
    await supabase.from("financial_snapshot").update({group_name:newName}).eq("group_name", oldName);
  }

  // Build groups
  const groups = [];
  const seen = new Set();
  [...rows].sort((a,b)=>a.group_order-b.group_order).forEach(r => {
    if (!seen.has(r.group_name)) { seen.add(r.group_name); groups.push(r.group_name); }
  });

  if (loading) return <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{...mono,color:T.mute}}>Loading</span></div>;

  return (
    <div style={{ flex:1, overflowY:"auto", padding:"28px 32px" }}>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24, paddingBottom:14, borderBottom:`1px solid ${T.border}` }}>
        <div>
          <div style={{ ...mono, fontSize:9, color:T.mute, marginBottom:4 }}>FİNANSAL DURUM</div>
          <div style={{ fontSize:20, fontWeight:600, letterSpacing:"-0.02em" }}>Güncel Tablo</div>
        </div>
        {addingGroup ? (
          <div style={{ display:"flex", gap:6 }}>
            <input value={newGroupName} onChange={e=>setNewGroupName(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter")addGroup();if(e.key==="Escape"){setAddingGroup(false);setNewGroupName("");}}}
              autoFocus placeholder="Grup adı…"
              style={{ background:"#fff", border:`1px solid ${T.accent}`, borderRadius:2, padding:"6px 12px", fontSize:12, fontFamily:"'Inter',sans-serif", width:180, outline:"none" }}/>
            <button onClick={addGroup} style={{ background:T.ink, color:"#fff", border:"none", borderRadius:2, padding:"6px 14px", fontSize:11, cursor:"pointer" }}>Ekle</button>
            <button onClick={()=>{setAddingGroup(false);setNewGroupName("");}} style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:2, padding:"6px 12px", fontSize:11, cursor:"pointer", color:T.mute }}>İptal</button>
          </div>
        ) : (
          <button onClick={()=>setAddingGroup(true)} style={{ background:T.ink, color:"#fff", border:"none", borderRadius:2, padding:"7px 16px", fontSize:11, cursor:"pointer", fontWeight:500 }}>+ Grup Ekle</button>
        )}
      </div>

      {rows.length===0 && (
        <div style={{ ...mono, color:T.mute, padding:"40px 0", textAlign:"center" }}>Henüz veri yok.</div>
      )}

      {/* Masonry-style columns */}
      <div style={{ columns:"2 400px", columnGap:16 }}>
        {groups.map(g => {
          const gRows = rows.filter(r=>r.group_name===g).sort((a,b)=>a.row_order-b.row_order);
          return (
            <div key={g} style={{ marginBottom:16, breakInside:"avoid" }}>
              <GroupCard
                group={g}
                rows={gRows}
                onUpdateRow={updateRow}
                onDeleteRow={deleteRow}
                onAddRow={addRow}
                onDeleteGroup={deleteGroup}
                onRenameGroup={renameGroup}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
